/**
 * Selector de empresa activa.
 *
 * En este ERP una `Company` es un consorcio: cambiarla cambia TODO lo que se ve.
 * Por eso el control es explicito, muestra siempre el nombre completo de la
 * empresa en curso y no se esconde detras de un icono.
 *
 * El tenant no se elige nunca desde el cliente: sale del usuario autenticado.
 *
 * Accesibilidad: patron listbox del APG — boton con `aria-haspopup="listbox"`,
 * lista con `aria-activedescendant`, flechas para moverse, Enter/Espacio para
 * elegir, Escape para cerrar devolviendo el foco al boton.
 */

import clsx from 'clsx';
import { useEffect, useId, useRef, useState } from 'react';

import { IconBuilding, IconCheck, IconChevronUpDown, IconSpinner } from '@/components/shell/icons';
import { notify } from '@/components/shell/toast';
import { selectActiveCompany, selectCompanies, useSessionStore } from '@/store/session';

export function CompanySwitcher({ className }: { className?: string }) {
  const companies = useSessionStore(selectCompanies);
  const activa = useSessionStore(selectActiveCompany);
  const tenant = useSessionStore((estado) => estado.tenant);
  const setActiveCompany = useSessionStore((estado) => estado.setActiveCompany);

  const [abierto, setAbierto] = useState(false);
  const [cambiando, setCambiando] = useState(false);
  const [indice, setIndice] = useState(0);

  const idLista = useId();
  const contenedorRef = useRef<HTMLDivElement>(null);
  const botonRef = useRef<HTMLButtonElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);

  // Cierre por click afuera. `pointerdown` y no `click` para que cerrar no
  // dispare tambien el control que hay debajo.
  useEffect(() => {
    if (!abierto) return;
    const alApuntar = (evento: PointerEvent) => {
      if (!contenedorRef.current?.contains(evento.target as Node)) setAbierto(false);
    };
    document.addEventListener('pointerdown', alApuntar);
    return () => {
      document.removeEventListener('pointerdown', alApuntar);
    };
  }, [abierto]);

  useEffect(() => {
    if (abierto) {
      setIndice(Math.max(0, companies.findIndex((company) => company.id === activa?.id)));
      listaRef.current?.focus();
    }
  }, [abierto, companies, activa]);

  const elegir = async (companyId: number) => {
    setAbierto(false);
    botonRef.current?.focus();
    if (companyId === activa?.id) return;

    setCambiando(true);
    const listo = await setActiveCompany(companyId);
    setCambiando(false);

    const elegida = companies.find((company) => company.id === companyId);
    if (listo) {
      notify.success('Empresa activa', elegida?.name ?? `#${companyId}`);
    } else {
      notify.error('No se pudo cambiar de empresa', 'Se mantuvo la empresa anterior.');
    }
  };

  const etiqueta = activa?.name ?? (companies.length === 0 ? 'Sin empresas' : 'Elegir empresa');

  // Con una sola empresa el desplegable no aporta: se muestra como dato.
  if (companies.length <= 1) {
    return (
      <div
        className={clsx('flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm', className)}
        title={tenant === null ? undefined : `Organización: ${tenant.name}`}
      >
        <IconBuilding className="h-4 w-4 shrink-0 text-fg-faint" />
        <span className="truncate font-medium">{etiqueta}</span>
      </div>
    );
  }

  return (
    <div ref={contenedorRef} className={clsx('relative', className)}>
      <button
        ref={botonRef}
        type="button"
        onClick={() => {
          setAbierto((valor) => !valor);
        }}
        onKeyDown={(evento) => {
          if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
            evento.preventDefault();
            setAbierto(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-controls={abierto ? idLista : undefined}
        aria-label={`Empresa activa: ${etiqueta}. Cambiar.`}
        disabled={cambiando}
        className="flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-line bg-surface px-2.5 text-sm font-medium transition-colors hover:border-line-strong hover:bg-surface-raised disabled:opacity-60"
      >
        {cambiando ? (
          <IconSpinner className="h-4 w-4 shrink-0 text-fg-faint" />
        ) : (
          <IconBuilding className="h-4 w-4 shrink-0 text-fg-faint" />
        )}
        <span className="truncate">{etiqueta}</span>
        <IconChevronUpDown className="ml-auto h-4 w-4 shrink-0 text-fg-faint" />
      </button>

      {abierto && (
        <div
          ref={listaRef}
          id={idLista}
          role="listbox"
          tabIndex={-1}
          aria-label="Empresas disponibles"
          aria-activedescendant={`${idLista}-${indice}`}
          onKeyDown={(evento) => {
            if (evento.key === 'Escape') {
              evento.preventDefault();
              setAbierto(false);
              botonRef.current?.focus();
            } else if (evento.key === 'ArrowDown') {
              evento.preventDefault();
              setIndice((actual) => (actual + 1) % companies.length);
            } else if (evento.key === 'ArrowUp') {
              evento.preventDefault();
              setIndice((actual) => (actual - 1 + companies.length) % companies.length);
            } else if (evento.key === 'Home') {
              evento.preventDefault();
              setIndice(0);
            } else if (evento.key === 'End') {
              evento.preventDefault();
              setIndice(companies.length - 1);
            } else if (evento.key === 'Enter' || evento.key === ' ') {
              evento.preventDefault();
              const elegida = companies[indice];
              if (elegida !== undefined) void elegir(elegida.id);
            } else if (evento.key === 'Tab') {
              setAbierto(false);
            }
          }}
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-72 overflow-y-auto rounded-lg border border-line bg-surface p-1 shadow-lg animate-slide-up"
        >
          {tenant !== null && (
            <p
              role="presentation"
              className="px-2.5 pb-1 pt-1.5 text-2xs font-semibold uppercase tracking-wide text-fg-faint"
            >
              {tenant.name}
            </p>
          )}
          {companies.map((company, posicion) => {
            const seleccionada = company.id === activa?.id;
            return (
              <div
                key={company.id}
                id={`${idLista}-${posicion}`}
                role="option"
                aria-selected={seleccionada}
                onClick={() => {
                  void elegir(company.id);
                }}
                onPointerEnter={() => {
                  setIndice(posicion);
                }}
                className={clsx(
                  'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm',
                  posicion === indice ? 'bg-accent-soft text-accent' : 'text-fg',
                )}
              >
                <span className="truncate">{company.name}</span>
                {seleccionada && <IconCheck className="ml-auto h-4 w-4 shrink-0 text-accent" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
